import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, ExternalLink, RotateCcw } from "lucide-react";
import { AdminListShell } from "@/components/admin/AdminListShell";
import { siteSettingsQuery, INVALIDATE } from "@/lib/query-registry";
import { useAuthedMutation } from "@/lib/authed-mutation";
import { updateHomeThemeDetails } from "@/lib/site-settings.functions";
import {
  DEFAULT_HOME_THEME_DETAILS,
  HOME_THEME_DETAIL_FIELDS,
  HomeThemeDetailsSchema,
  normalizeHomeThemeDetails,
  type HomeThemeDetails,
} from "@/lib/home-theme-details";

export const Route = createFileRoute("/admin/settings/home-visuals")({
  head: () => ({
    meta: [
      { title: "Home visuals · Settings · Admin · Shugert" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: HomeVisualsPage,
});

const GROUPS = ["Header and clients", "Press coverage", "Final CTA"] as const;

function hexToRgb(hex: string) {
  const normalized = /^#[0-9a-f]{6}$/i.test(hex) ? hex.slice(1) : "000000";
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function luminance(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  const channels = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrastRatio(a: string, b: string) {
  const first = luminance(a);
  const second = luminance(b);
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

function HomeVisualsPage() {
  const router = useRouter();
  const query = useQuery(siteSettingsQuery());
  const [settings, setSettings] = useState<HomeThemeDetails>(DEFAULT_HOME_THEME_DETAILS);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSettings(normalizeHomeThemeDetails(query.data?.settings?.theme_colors?.home_details));
  }, [query.data]);

  const storageReady = query.data?.settings?.theme_storage_ready === true;
  const validation = useMemo(() => HomeThemeDetailsSchema.safeParse(settings), [settings]);
  const contrastChecks = useMemo(() => [
    {
      label: "Client-logo heading on white",
      ratio: contrastRatio(settings.clients_heading_color, "#FFFFFF"),
      minimum: 4.5,
    },
    {
      label: "Press eyebrow on section background",
      ratio: contrastRatio(settings.press_eyebrow_color, settings.press_section_bg),
      minimum: 4.5,
    },
    {
      label: "Press body on section background",
      ratio: contrastRatio(settings.press_body_color, settings.press_section_bg),
      minimum: 4.5,
    },
    {
      label: "Final CTA eyebrow on white",
      ratio: contrastRatio(settings.cta_eyebrow_color, "#FFFFFF"),
      minimum: 4.5,
    },
  ], [settings]);
  const hasContrastFailure = contrastChecks.some((check) => check.ratio < check.minimum);
  const canSave = storageReady && validation.success && !hasContrastFailure;

  const mutation = useAuthedMutation(updateHomeThemeDetails, {
    invalidate: [INVALIDATE.siteSettings],
    invalidateOn: "settled",
    onSuccess: () => {
      setSaved(true);
      setError(null);
      router.invalidate();
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err) => setError(err.message || "Failed to publish Home visual settings"),
  });

  const update = <K extends keyof HomeThemeDetails>(key: K, value: HomeThemeDetails[K]) => {
    setSaved(false);
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const publish = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaved(false);
    if (!storageReady) {
      setError("Theme storage is not installed. Run the theme migration before publishing.");
      return;
    }
    if (!canSave) {
      setError("Fix invalid HEX values and contrast warnings before publishing.");
      return;
    }
    mutation.mutate(settings);
  };

  const previewStyle = {
    "--hero-rule": settings.hero_rule_color,
    "--clients-heading": settings.clients_heading_color,
    "--press-bg": settings.press_section_bg,
    "--press-border": settings.press_section_border_color,
    "--press-eyebrow": settings.press_eyebrow_color,
    "--press-body": settings.press_body_color,
    "--press-logo-bg": settings.press_logo_bg,
    "--press-logo-border": settings.press_logo_border_color,
    "--cta-rule": settings.cta_rule_color,
    "--cta-eyebrow": settings.cta_eyebrow_color,
  } as CSSProperties;

  return (
    <div className="space-y-7">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Home · explicit controls</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground">Home visual details</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            These controls correspond exactly to the visible elements on the homepage: the line below navigation, client-logo heading, press section and final CTA rules.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/" target="_blank" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-card">
            Open Home <ExternalLink className="h-3.5 w-3.5" />
          </Link>
          <Link to="/admin/settings/theme" className="text-sm text-muted-foreground hover:text-foreground">Main theme editor</Link>
        </div>
      </header>

      {!storageReady ? (
        <div className="rounded-xl border border-amber-600/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900">
          Theme storage is not connected. Run <code>supabase/migrations/20260729171000_dynamic_theme_colors.sql</code> first.
        </div>
      ) : null}

      <AdminListShell query={query} skeletonRows={8}>
        {() => (
          <form onSubmit={publish} className="grid gap-8 xl:grid-cols-[minmax(0,1.25fr)_minmax(22rem,0.75fr)]">
            <div className="space-y-7">
              {GROUPS.map((group) => (
                <section key={group} className="overflow-hidden rounded-2xl border border-border bg-card/20">
                  <header className="border-b border-border px-5 py-4">
                    <h2 className="font-display text-lg font-semibold text-foreground">{group}</h2>
                  </header>
                  <div className="divide-y divide-border">
                    {HOME_THEME_DETAIL_FIELDS.filter((field) => field.group === group).map((field) => {
                      const fieldError = validation.success
                        ? null
                        : validation.error.issues.find((issue) => issue.path[0] === field.key)?.message;
                      return (
                        <div key={field.key} className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_13rem] md:items-center">
                          <div>
                            <label htmlFor={`home-detail-${field.key}`} className="text-sm font-semibold text-foreground">{field.label}</label>
                            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{field.description}</p>
                          </div>
                          <ColorControl
                            id={`home-detail-${field.key}`}
                            label={field.label}
                            value={settings[field.key]}
                            onChange={(value) => update(field.key, value.toUpperCase())}
                            error={fieldError}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {group === "Header and clients" ? (
                    <ToggleControl
                      label="Show rule below navigation"
                      description="Disables the line completely without changing its saved color."
                      checked={settings.hero_rule_enabled}
                      onChange={(value) => update("hero_rule_enabled", value)}
                    />
                  ) : null}
                  {group === "Press coverage" ? (
                    <div className="grid gap-px border-t border-border bg-border sm:grid-cols-2">
                      <ToggleControl
                        label="Show press-section borders"
                        description="Top and bottom lines around the complete press area."
                        checked={settings.press_section_border_enabled}
                        onChange={(value) => update("press_section_border_enabled", value)}
                      />
                      <ToggleControl
                        label="Show press-logo plates"
                        description="Solid plate behind each press logo. Off means logos sit directly on the section background."
                        checked={settings.press_logo_plate_enabled}
                        onChange={(value) => update("press_logo_plate_enabled", value)}
                      />
                      <ToggleControl
                        label="Show press-logo borders"
                        description="Outline around each white logo plate."
                        checked={settings.press_logo_border_enabled}
                        onChange={(value) => update("press_logo_border_enabled", value)}
                      />
                    </div>
                  ) : null}
                  {group === "Final CTA" ? (
                    <ToggleControl
                      label="Show final CTA rules"
                      description="Disables the top, accent and bottom lines around the final CTA."
                      checked={settings.cta_rule_enabled}
                      onChange={(value) => update("cta_rule_enabled", value)}
                    />
                  ) : null}
                </section>
              ))}

              <button
                type="button"
                onClick={() => setSettings({ ...DEFAULT_HOME_THEME_DETAILS })}
                className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Reset Shopify Experts defaults
              </button>
            </div>

            <aside className="space-y-5 xl:sticky xl:top-6 xl:self-start" style={previewStyle}>
              <section className="overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
                <div className="h-px" style={{ background: settings.hero_rule_enabled ? settings.hero_rule_color : "transparent" }} />
                <div className="p-6">
                  <p className="text-center text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: settings.clients_heading_color }}>
                    Selected DTC and B2B teams we have supported
                  </p>
                  <div className="mt-6 flex items-center justify-around text-xs text-muted-foreground">
                    <span>CLIENT 01</span><span>CLIENT 02</span><span>CLIENT 03</span>
                  </div>
                </div>
              </section>

              <section
                className="rounded-2xl p-5 shadow-sm"
                style={{
                  background: settings.press_section_bg,
                  borderTop: `${settings.press_section_border_enabled ? 1 : 0}px solid ${settings.press_section_border_color}`,
                  borderBottom: `${settings.press_section_border_enabled ? 1 : 0}px solid ${settings.press_section_border_color}`,
                }}
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: settings.press_eyebrow_color }}>Recognition and coverage</p>
                <p className="mt-2 text-xs leading-relaxed" style={{ color: settings.press_body_color }}>Independent coverage and industry recognition remain part of the public record.</p>
                <div className="mt-5 grid grid-cols-3 gap-2">
                  {["Forbes", "AP", "Europa"].map((logo) => (
                    <div
                      key={logo}
                      className="rounded-lg px-2 py-3 text-center text-xs font-semibold"
                      style={{
                        background: settings.press_logo_plate_enabled ? settings.press_logo_bg : "transparent",
                        border: `${settings.press_logo_border_enabled ? 1 : 0}px solid ${settings.press_logo_border_color}`,
                      }}
                    >{logo}</div>
                  ))}
                </div>
              </section>

              <section className="rounded-2xl bg-white p-5 shadow-sm">
                <div
                  className="py-5"
                  style={{
                    borderTop: `${settings.cta_rule_enabled ? 1 : 0}px solid ${settings.cta_rule_color}`,
                    borderBottom: `${settings.cta_rule_enabled ? 1 : 0}px solid ${settings.cta_rule_color}`,
                  }}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: settings.cta_eyebrow_color }}>Start with the business requirement</p>
                  <h2 className="mt-3 font-display text-2xl font-semibold text-foreground">Show us what Shopify needs to support next.</h2>
                </div>
              </section>

              <section className="rounded-2xl border border-border bg-card/30 p-5">
                <h2 className="font-display text-lg font-semibold text-foreground">Contrast checks</h2>
                <div className="mt-4 space-y-3">
                  {contrastChecks.map((check) => {
                    const passes = check.ratio >= check.minimum;
                    return (
                      <div key={check.label} className="flex items-center justify-between gap-4 text-xs">
                        <span className="text-muted-foreground">{check.label}</span>
                        <span className={passes ? "font-medium text-emerald-700" : "font-medium text-destructive"}>
                          {passes ? "Pass" : "Fail"} · {check.ratio.toFixed(2)}:1
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>

              {error ? <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div> : null}
              {saved ? (
                <div className="flex items-start gap-2 rounded-xl border border-emerald-600/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" /> Published. Public pages pick up these values within 5 minutes.
                </div>
              ) : null}

              <button
                type="submit"
                disabled={mutation.isPending || !canSave}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-semibold text-background hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {mutation.isPending ? "Publishing…" : "Publish Home details"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </aside>
          </form>
        )}
      </AdminListShell>
    </div>
  );
}

function ColorControl({
  id,
  label,
  value,
  onChange,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
}) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          aria-label={`${label} picker`}
          type="color"
          value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#000000"}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-12 cursor-pointer rounded-md border border-border bg-background p-1"
        />
        <input
          id={id}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          spellCheck={false}
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 font-mono text-sm uppercase text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function ToggleControl({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-5 bg-background p-5">
      <span>
        <span className="block text-sm font-semibold text-foreground">{label}</span>
        <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{description}</span>
      </span>
      <span className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-foreground" : "bg-muted"}`}>
        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="sr-only" />
        <span className={`absolute top-1 h-4 w-4 rounded-full bg-background transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </span>
    </label>
  );
}
